import React, { useEffect, useState } from 'react';
import useTracking from '../../../../../common/src/hooks/tracking/useTracking';
import ComparisonMatrix from '../../comparison/components/comparison-matrix/ComparisonMatrix';
import {
  addToSelectedOffers,
  addVisitedOverviewPage,
  removeFromSelectedOffers,
  setTerminationLetterModal,
} from '../../../store/redux/slices/offersSlice';
import {
  useLouiseDispatch,
  useLouiseSelector,
} from '../../../store/redux/store';
import useInitialOffersPageData from '../hooks/useInitialOffersPageData';
import useOffersPageParams from '../hooks/useOffersPageParams';
import AddToOffersModal from '../../../components/Modal/AddToOffersModal';
import useIsNewRoute from '../hooks/useIsNewRoute';
import OffersOverviewContainer from '../components/overview/overview-container/OffersOverviewContainer';
import NewOffersOverviewContainer from '../components/overview/overview-container/NewOffersOverviewContainer';
import useSelectedQuotesStore from '../store/zustand/selectedQuotes';

const OffersOverview: React.FC = () => {
  const dispatch = useLouiseDispatch();
  const { trackEvent } = useTracking();
  const params = useOffersPageParams();
  const riskObject = params.riskObject!;
  const [selectedQuoteId, setSelectedQuoteId] = useState<string>();
  const [isComparisonActive, setIsComparisonActive] = useState<boolean>(false);
  const isNewRoute = useIsNewRoute();

  useInitialOffersPageData();

  const visitedOverviewPages = useLouiseSelector(
    (state) => state.offers.visitedOverviewPages,
  );
  const terminationLetterModalType = useLouiseSelector(
    (state) => state.offers.terminationLetterModalType,
  );
  const selectedOffers = useLouiseSelector(
    (state) => state.offers.selectedOffers,
  );
  const resetSelectedQuotes = useSelectedQuotesStore((state) => state.clear);

  const offersInfo = useLouiseSelector((state) => state.offers.offersInfo);

  useEffect(() => {
    return () => {
      resetSelectedQuotes();
      if (riskObject && visitedOverviewPages) {
        dispatch(addVisitedOverviewPage(riskObject));
      }
    };
  }, [riskObject]);

  const handleAddToSelectedOffers = (id?: string, showModal = true) => {
    if (!id) return;
    if (
      showModal &&
      !offersInfo.insuranceTerminationInfo[riskObject] &&
      riskObject !== 'funeral'
    ) {
      dispatch(setTerminationLetterModal(riskObject));
      setSelectedQuoteId(id);
    } else {
      trackEvent('Add offer to basket in Sales Conversation');
      dispatch(addToSelectedOffers({ riskObject, id }));
    }
  };

  const handleRemoveFromSelectedOffers = (id?: string) => {
    if (!id) return;
    if (selectedOffers[riskObject]?.includes(id))
      dispatch(removeFromSelectedOffers({ riskObject, id }));
  };

  return (
    <>
      {terminationLetterModalType && (
        <AddToOffersModal
          riskObject={terminationLetterModalType}
          quoteId={selectedQuoteId}
          showModal={!!terminationLetterModalType}
          onClose={() => dispatch(setTerminationLetterModal(''))}
          addToSelectedOffers={(showModal) =>
            handleAddToSelectedOffers(selectedQuoteId, showModal)
          }
        />
      )}

      {/* HIDDEN UNTIL WE HAVE THE REQUIRED INFORMATION FOR MAKING THIS USEFUL */}
      {/* FOR MORE INFO ASK MICHIEL */}
      <ComparisonMatrix
        riskObject={riskObject}
        isComparisonActive={isComparisonActive}
        setIsComparisonActive={setIsComparisonActive}
        addToSelectedOffers={handleAddToSelectedOffers}
        removeFromSelectedOffers={handleRemoveFromSelectedOffers}
      />

      {isNewRoute ? (
        <NewOffersOverviewContainer
          riskObject={riskObject!}
          addToSelectedOffers={handleAddToSelectedOffers}
          removeFromSelectedOffers={handleRemoveFromSelectedOffers}
          setIsComparisonActive={setIsComparisonActive}
        />
      ) : (
        <OffersOverviewContainer
          riskObject={riskObject!}
          addToSelectedOffers={handleAddToSelectedOffers}
          removeFromSelectedOffers={handleRemoveFromSelectedOffers}
          setIsComparisonActive={setIsComparisonActive}
          isProductsLoading={false}
        />
      )}
    </>
  );
};

export default OffersOverview;
